import React from 'react';
import PropTypes from 'prop-types';
import { formatGameDateTime } from '../../utils/timeUtils';
import {
  formatForecastAge,
  formatWeatherContext,
  formatWindCompass,
  getWeatherIcon,
} from '../../utils/gameDetailUtils';
import styles from './GameDetailModal.module.css';

const formatSurface = (venue) => {
  if (venue.dome) return 'Dome';
  if (venue.grass === true) return 'Natural grass';
  if (venue.grass === false) return 'Artificial turf';
  return null;
};

const DetailSection = ({ title, children, muted = false }) => (
  <div className={`${styles.weatherSection} ${muted ? styles.weatherSectionMuted : ''}`}>
    {title && <h3 className={styles.weatherSectionTitle}>{title}</h3>}
    {children}
  </div>
);

DetailSection.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  muted: PropTypes.bool,
};

const GameDetailWeather = ({ game }) => {
  const weather = game.weather || {};
  const venue = game.venue || {};
  const { displayDate, displayTime } = formatGameDateTime(game.start_date);
  const isIndoor = Boolean(weather.game_indoors || venue.dome);

  const contextLine = formatWeatherContext(game, displayDate, displayTime);
  const forecastAge = game.game_status === 'scheduled'
    ? formatForecastAge(weather.forecast_refreshed_at)
    : null;

  const windCompass = formatWindCompass(weather.wind_direction);
  const windValue = weather.wind_speed != null
    ? `${weather.wind_speed} mph${windCompass ? ` ${windCompass}` : ''}`
    : null;

  const weatherRows = [
    { label: 'Condition', value: weather.condition, icon: getWeatherIcon(weather.condition) },
    { label: 'Temperature', value: weather.temperature != null ? `${weather.temperature}°F` : null },
    { label: 'Wind', value: windValue },
    { label: 'Humidity', value: weather.humidity != null ? `${weather.humidity}%` : null },
    { label: 'Precipitation', value: weather.precipitation != null ? `${weather.precipitation}` : null },
    { label: 'Dew Point', value: weather.dew_point != null ? `${weather.dew_point}°F` : null },
    { label: 'Pressure', value: weather.pressure != null ? `${weather.pressure} mb` : null },
  ].filter((row) => row.value != null && row.value !== '');

  const environmentRows = [
    { label: 'Stadium', value: venue.name },
    { label: 'Location', value: venue.city && venue.state ? `${venue.city}, ${venue.state}` : null },
    { label: 'Capacity', value: venue.capacity != null ? venue.capacity.toLocaleString() : null },
    { label: 'Surface', value: formatSurface(venue) },
    { label: 'Elevation', value: venue.elevation != null ? `${venue.elevation} ft` : null },
    { label: 'Setting', value: isIndoor ? 'Indoor / Dome' : 'Outdoor' },
  ].filter((row) => row.value != null && row.value !== '');

  if (weatherRows.length === 0 && environmentRows.length === 0) {
    return (
      <div className={styles.section}>
        <p className={styles.emptyState}>Weather and venue details are not available for this game.</p>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      {contextLine && (
        <p className={styles.weatherContext}>{contextLine}</p>
      )}
      {forecastAge && (
        <p className={styles.weatherTimestamp}>{forecastAge}</p>
      )}
      {game.game_status === 'in_progress' && (
        <p className={styles.weatherTimestamp}>Live conditions — updates with the scoreboard feed.</p>
      )}

      {isIndoor && (
        <div className={styles.indoorNotice}>
          Indoor venue — weather has minimal impact on play conditions.
        </div>
      )}

      {weatherRows.length > 0 && (
        <DetailSection title="Weather" muted={isIndoor}>
          <dl className={styles.detailList}>
            {weatherRows.map(({ label, value, icon }) => (
              <div key={label} className={styles.detailRow}>
                <dt>{label}</dt>
                <dd className={styles.detailValueWithIcon}>
                  {icon && <span className={styles.conditionIcon} aria-hidden>{icon}</span>}
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </DetailSection>
      )}

      {environmentRows.length > 0 && (
        <DetailSection title="Game Environment">
          <dl className={styles.detailList}>
            {environmentRows.map(({ label, value }) => (
              <div key={label} className={styles.detailRow}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </DetailSection>
      )}
    </div>
  );
};

GameDetailWeather.propTypes = {
  game: PropTypes.object.isRequired,
};

export default GameDetailWeather;
